import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { FormGroup } from 'react-bootstrap';
import { MultiSelect } from 'react-multi-select-component';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, set, concat } from 'lodash';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import * as suggestionTodoListAction from '../store/Suggestion.action';
import { Year, createOptions, checkArray, Page_Size } from '../../../../../Components/helper';
import { getSupplierBrandsAction } from '../../Costing/store/Costing.action';
import * as systemMasterAction from '../../../../SystemMasters/Store/SystemMasters.action';

const customStyles = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  container: () => ({
    // none of react-select's styles are passed to <Control />
    flex: '1',
    minWidth: 200,
    marginRight: 10,
  }),
  control: () => ({
    // none of react-select's styles are passed to <Control />
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: '24px',
    height: '24px',
    border: 'none',
    borderBottom: '1px solid #848484',
    borderRadius: 0,
    boxShadow: 'none',
    fontSize: 'var(--16px)',
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = 'opacity 300ms';

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: '-9px 0 0 0',
    border: '1px solid #848484',
    borderRadius: 0,
    padding: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '0 4px',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '0 4px',
  }),
};

function FilterSuggestionList() {
  const { t } = useTranslation('common');
  const { t: tReports } = useTranslation('todo-reports');

  const dispatch = useDispatch();
  const reducers = useSelector((redState) => redState);
  const {
    UserDetails: {
      isSupplierLoggedIn,
      isCompanyUserLoggedIn,
      userInfo: { userId },
    },
    SystemMastersDetails: { multiSubBrandList, pcTypeList, licensorList, licenseList, likenessList },
    SuggestionTodoListData: {
      section,
      suggestionFilterValues: {
        selectedSuggestion_Season,
        selectedSuggestion_Year,
        selectedSuggestion_Brand,
        selectedSuggestion_SubBrand,
        selectedSuggestion_Supplier,
        selectedSuggestion_Category1,
        selectedSuggestion_Category2,
        selectedSuggestion_Category3,
        selectedSuggestion_Category4,
        selectedSuggestion_Section,
        selectedSuggestion_Status,
        selectedSuggestion_ActionType,
        selectedSuggestion_ProductType,
        selectedLicensor,
        selectedLicense,
        selectedLikeness,
        sortOrder,
        sortPropertyName,
      },
    },
  } = reducers;

  const [state, setState] = useState({
    seasonOptions: [],
    yearOptions: createOptions(Year, 'name', '_id'),
    brandOptions: [],
    subBrandOptions: [],
    supplierOptions: [],
    sampleTypeOptions: [],
    sampleStatusOptions: [],
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    sectionOptions: [],
    statusOptions: [],
    actionTypeOptions: [],
    productTypeOptions: [],
  });
  const {
    seasonOptions,
    brandOptions,
    subBrandOptions,
    supplierOptions,
    category1Options,
    category2Options,
    category3Options,
    category4Options,
    sectionOptions,
    statusOptions,
    actionTypeOptions,
    productTypeOptions,
  } = state;

  const [isApply, setIsApply] = useState(false);
  const [subBrandAccess, setSubBrandAccess] = useState(false);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [licensorAccess, setLicensorAccess] = useState(false);
  // Prevent re-entrant fetch loops (same pattern as IndicatorFilter)
  const isFetchingCatsRef = useRef(false);

  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      const subBrandAccessRes = await dispatch(systemMasterAction.getSubBrandAccess());
      setSubBrandAccess(subBrandAccessRes?.data || false);
    };
    fetchSubBrandAccess();
  }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    const fetchLicensorSetup = async () => {
      const res = await dispatch(systemMasterAction.getLicensorAccess()).catch(() => null);
      const hasAccess = !!res?.isActiveLicensor;
      setLicensorAccess(hasAccess);
      if (hasAccess) {
        dispatch(systemMasterAction.getLicensorDetails({ loggedInUserId: userId, isActive: true }));
      }
    };
    fetchLicensorSetup();
  }, [dispatch, userId]);

  useEffect(() => {
    if (!userId) return;
    const saved = localStorage.getItem('suggestionTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.selectedLicensor?.length > 0 || parsed?.selectedLicense?.length > 0 || parsed?.selectedLikeness?.length > 0) {
        dispatch(
          suggestionTodoListAction.filterSortData({
            selectedLicensor: parsed.selectedLicensor || [],
            selectedLicense: parsed.selectedLicense || [],
            selectedLikeness: parsed.selectedLikeness || [],
          }),
        );
      }
      if (parsed?.selectedLicensor?.length > 0) {
        dispatch(
          systemMasterAction.getLicenseDetails({
            licensorIds: parsed.selectedLicensor,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
      if (parsed?.selectedLicensor?.length > 0 && parsed?.selectedLicense?.length > 0) {
        dispatch(
          systemMasterAction.getLikenessDetails({
            licensorIds: parsed.selectedLicensor,
            licenseIds: parsed.selectedLicense,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  // Fetch feature flag for cascading by Product Type
  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  // Ensure Product Types (Tier1) are available for the Product Type MultiSelect
  useEffect(() => {
    dispatch(
      systemMasterAction.getPCTypesDetails(
        {
          loggedInUserId: userId,
          isActive: true,
          tier: 'Tier1',
        },
        true,
      ),
    );
  }, [dispatch, userId]);

  // Build product type options locally from store list
  useEffect(() => {
    if (Array.isArray(pcTypeList) && pcTypeList.length > 0) {
      setState((prev) => ({ ...prev, productTypeOptions: createOptions(pcTypeList, 'name', '_id') }));
    } else {
      setState((prev) => ({ ...prev, productTypeOptions: [] }));
    }
  }, [pcTypeList]);

  // Helpers for cascading filtering (same as reference file)
  const getIds = (arr) => (Array.isArray(arr) ? arr.filter(Boolean) : []);
  const selectedPTIds = getIds(selectedSuggestion_ProductType);
  const selectedCat1Ids = getIds(selectedSuggestion_Category1);
  const selectedCat2Ids = getIds(selectedSuggestion_Category2);
  const selectedCat3Ids = getIds(selectedSuggestion_Category3);

  // Stable keys to avoid effect loops due to referential changes
  const selCat1Key = useMemo(() => (selectedCat1Ids.length ? selectedCat1Ids.slice().sort().join(',') : ''), [selectedCat1Ids]);
  const selCat2Key = useMemo(() => (selectedCat2Ids.length ? selectedCat2Ids.slice().sort().join(',') : ''), [selectedCat2Ids]);
  const selCat3Key = useMemo(() => (selectedCat3Ids.length ? selectedCat3Ids.slice().sort().join(',') : ''), [selectedCat3Ids]);

  // Equality helpers to avoid redundant setState
  const optionIds = (arr) =>
    Array.isArray(arr)
      ? arr
          .map((o) => String(o?._id ?? o?.value))
          .filter(Boolean)
          .sort()
      : [];
  const areOptionIdsEqual = (a, b) => {
    const aa = optionIds(a);
    const bb = optionIds(b);
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i += 1) if (aa[i] !== bb[i]) return false;
    return true;
  };

  // Client-side filtering using useMemo (same approach as reference file)
  const category1Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category1Options;
    if (!selectedPTIds?.length) return category1Options;
    const base = category1Options || [];
    const filtered = base.filter((opt) => {
      const used = Array.isArray(opt?.isUsedProductType) ? opt.isUsedProductType : [];
      const pts = Array.isArray(opt?.productType) ? opt.productType.map((p) => (typeof p === 'object' ? p?._id : p)).filter(Boolean) : [];
      return used?.some((id) => selectedPTIds.includes(id)) || pts?.some((id) => selectedPTIds.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category1Options, selectedPTIds]);

  const category2Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category2Options;
    if (!selectedCat1Ids?.length) return category2Options;
    const base = category2Options || [];
    const hasMeta = base.some((opt) => Array.isArray(opt?.isUsedCat1) && opt.isUsedCat1.length);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => {
      const usedCat1 = Array.isArray(opt?.isUsedCat1) ? opt.isUsedCat1 : [];
      return usedCat1?.some((id) => selectedCat1Ids.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category2Options, selectedCat1Ids]);

  const category3Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category3Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length) return category3Options;
    const base = category3Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => selectedCat1Ids.includes(opt?.categoryOneId) && selectedCat2Ids.includes(opt?.categoryTwoId));
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category3Options, selectedCat1Ids, selectedCat2Ids]);

  const category4Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category4Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length || !selectedCat3Ids?.length) return category4Options;
    const base = category4Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId && opt?.categoryThreeId);
    if (!hasMeta) return base;
    const filtered = base.filter(
      (opt) => selectedCat1Ids.includes(opt?.categoryOneId) && selectedCat2Ids.includes(opt?.categoryTwoId) && selectedCat3Ids.includes(opt?.categoryThreeId),
    );
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category4Options, selectedCat1Ids, selectedCat2Ids, selectedCat3Ids]);

  useEffect(() => {
    const isTrue = JSON.parse(localStorage.getItem('suggestionTodoListFilter'));
    if (isTrue) {
      for (const keys in isTrue) {
        if (keys === 'selectedSuggestion_Status' || keys === 'selectedSuggestion_Section' || keys === 'selectedSuggestion_ActionType') {
          if (isTrue[keys]) {
            setIsApply(true);
          }
        } else if (isTrue[keys].length > 0) {
          setIsApply(true);
        }
      }
      setSta({
        status: isTrue.selectedSuggestion_Status
          ? {
              label: isTrue.selectedSuggestion_Status,
              value: isTrue.selectedSuggestion_Status,
            }
          : null,
        actionType: isTrue.selectedSuggestion_ActionType
          ? {
              label: isTrue.selectedSuggestion_ActionType,
              value: isTrue.selectedSuggestion_ActionType,
            }
          : null,
        section: isTrue.selectedSuggestion_ActionType
          ? {
              label: isTrue.selectedSuggestion_Section,
              value: isTrue.selectedSuggestion_Section,
            }
          : null,
      });
    }
  }, []);

  // Fetch subbrands when brand changes
  useEffect(() => {
    const brandId = selectedSuggestion_Brand;
    if (brandId) {
      dispatch(
        systemMasterAction.getMultiSubBrandDetails({
          brandId: Array.isArray(brandId) ? brandId : [brandId],
        }),
      );
    }
  }, [dispatch, selectedSuggestion_Brand]);

  // Map reducer sub-brand list to component options whenever it updates
  useEffect(() => {
    if (Array.isArray(multiSubBrandList) && multiSubBrandList.length > 0) {
      const opts = createOptions(multiSubBrandList, 'name', '_id');
      setState((prev) => ({ ...prev, subBrandOptions: opts }));
    } else {
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
  }, [multiSubBrandList]);

  const handleSelectChange = async (value, name) => {
    const obj = {};
    set(obj, `${name}`, value);

    // When Product Type changes and cascading is ON, clear all downstream category selections
    if (name === 'selectedSuggestion_ProductType' && catBasedOnPTData) {
      obj.selectedSuggestion_Category1 = [];
      obj.selectedSuggestion_Category2 = [];
      obj.selectedSuggestion_Category3 = [];
      obj.selectedSuggestion_Category4 = [];
    }

    // When Category 1 changes, also reset Category 2 and clear downstream options
    if (name === 'selectedSuggestion_Category1') {
      obj.selectedSuggestion_Category2 = [];
      // Clear Cat3/Cat4 options so UI doesn't show stale values
      setState((prev) => ({ ...prev, category3Options: [], category4Options: [] }));
    }

    if (
      ['selectedSuggestion_Category1', 'selectedSuggestion_Category2'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selectedSuggestion_Category3 = [];
      obj.selectedSuggestion_Category4 = [];
    }
    if (
      ['selectedSuggestion_Category3'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selectedSuggestion_Category4 = [];
    }
    if (
      ['selectedSuggestion_Brand'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selectedSuggestion_SubBrand = [];
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
    await dispatch(suggestionTodoListAction.filterSortData(obj));
  };

  const licensorOptions = useMemo(() => (checkArray(licensorList) ? createOptions(licensorList, 'name', '_id') : []), [licensorList]);
  const licenseOptions = useMemo(() => (checkArray(licenseList) ? createOptions(licenseList, 'name', '_id') : []), [licenseList]);
  const likenessOptions = useMemo(() => (checkArray(likenessList) ? createOptions(likenessList, 'name', '_id') : []), [likenessList]);

  const handleFilterLicense = (val, key) => {
    const newData = { [key]: val };
    if (key === 'selectedLicensor') {
      newData.selectedLicense = [];
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0) {
        dispatch(systemMasterAction.getLicenseDetails({ licensorIds: val, loggedInUserId: userId, isActive: true }));
      } else {
        dispatch(systemMasterAction.clearLicenseData());
      }
    }
    if (key === 'selectedLicense') {
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0 && checkArray(selectedLicensor)) {
        dispatch(systemMasterAction.getLikenessDetails({ licensorIds: selectedLicensor, licenseIds: val, loggedInUserId: userId, isActive: true }));
      }
    }
    dispatch(suggestionTodoListAction.filterSortData(newData));
  };

  const clearAll = async () => {
    setSta({ actionType: null, status: null, section: null });

    const paramsObj = {
      seasons: [],
      year: [],
      brand: [],
      subBrand: [],
      supplier: [],
      colour: [],
      status: null,
      category1: [],
      category2: [],
      category3: [],
      category4: [],
      section: null,
      actionType: null,
      licensorId: [],
      licenseId: [],
      likenessId: [],
      sortOrder,
      sortPropertyName,

      pageIndex: 1,
      pageSize: Page_Size,
      type: 'suggestion',
    };
    const sortinObj = {
      selectedSuggestion_Season: [],
      selectedSuggestion_Year: [],
      selectedSuggestion_Brand: [],
      selectedSuggestion_SubBrand: [],
      selectedSuggestion_Supplier: [],
      selectedSuggestion_ActionType: '',
      selectedSuggestion_Status: '',
      selectedSuggestion_Category1: [],
      selectedSuggestion_Category2: [],
      selectedSuggestion_Category3: [],
      selectedSuggestion_Category4: [],
      selectedSuggestion_ProductType: [],
      selectedSuggestion_Section: null,
      selectedLicensor: [],
      selectedLicense: [],
      selectedLikeness: [],
      loading: true,
      sortOrder,
      sortPropertyName,
      pageIndex: 1,
      pageSize: Page_Size,
    };
    dispatch(systemMasterAction.clearLicenseData());
    dispatch(systemMasterAction.clearLikenessData());
    const res = await dispatch(suggestionTodoListAction.getSuggestionList(paramsObj));
    localStorage.removeItem('suggestionTodoListFilter');
    setIsApply(false);
    if (res) await dispatch(suggestionTodoListAction.filterSortData(sortinObj));
  };

  const applyFilter = async () => {
    setIsApply(true);
    const paramsObj = {
      seasons: selectedSuggestion_Season,
      year: selectedSuggestion_Year,
      brand: selectedSuggestion_Brand,
      subBrand: selectedSuggestion_SubBrand,
      supplier: selectedSuggestion_Supplier,
      status: selectedSuggestion_Status,
      category1: selectedSuggestion_Category1,
      category2: selectedSuggestion_Category2,
      category3: selectedSuggestion_Category3,
      category4: selectedSuggestion_Category4,
      productTypeId: Array.isArray(selectedSuggestion_ProductType) ? selectedSuggestion_ProductType : [],
      section: selectedSuggestion_Section,
      actionType: selectedSuggestion_ActionType,
      licensorId: selectedLicensor,
      licenseId: selectedLicense,
      likenessId: selectedLikeness,
      loading: true,
      sortOrder,
      sortPropertyName,

      pageIndex: 1,
      pageSize: Page_Size,
      type: 'suggestion',
    };

    const paramsObj1 = {
      selectedSuggestion_Season,
      selectedSuggestion_Year,
      selectedSuggestion_Brand,
      selectedSuggestion_SubBrand,
      selectedSuggestion_Supplier,
      selectedSuggestion_Category1,
      selectedSuggestion_Category2,
      selectedSuggestion_Category3,
      selectedSuggestion_Category4,
      selectedSuggestion_ProductType,
      selectedSuggestion_Section,
      selectedSuggestion_Status,
      selectedSuggestion_ActionType,
      selectedLicensor,
      selectedLicense,
      selectedLikeness,
    };

    localStorage.setItem('suggestionTodoListFilter', JSON.stringify(paramsObj1));
    await dispatch(suggestionTodoListAction.getSuggestionList(paramsObj));
    const obj = {
      pageIndex: 1,
      pageSize: Page_Size,
    };
    await dispatch(suggestionTodoListAction.filterSortData(obj));
  };

  const fetchCategories = useCallback(async () => {
    // Hydrate Category 1 and 2 base lists only
    for (const cat of [1, 2]) {
      const res = await dispatch(
        suggestionTodoListAction.fetchCategoryAction({
          categoryType: cat,
          loggedInUserId: userId,
        }),
      );
      setState((prevState) => ({ ...prevState, [`category${cat}Options`]: createOptions(res, 'name', '_id') }));
    }

    // Hydrate Category 3 if Cat1+Cat2 are already selected (support multi Cat1/Cat2)
    if (
      Array.isArray(selectedSuggestion_Category1) &&
      selectedSuggestion_Category1.length &&
      Array.isArray(selectedSuggestion_Category2) &&
      selectedSuggestion_Category2.length
    ) {
      const c1s = selectedSuggestion_Category1;
      const c2s = selectedSuggestion_Category2;
      const calls3 = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          calls3.push(
            dispatch(
              suggestionTodoListAction.fetchCategoryAction({
                categoryType: 3,
                loggedInUserId: userId,
                categoryOneId: c1,
                categoryTwoId: c2,
                isActive: 1,
              }),
            ),
          );
        });
      });
      const results3 = await Promise.all(calls3);
      const merged3 = [];
      const seen3 = new Set();
      results3.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen3.has(key)) {
              seen3.add(key);
              merged3.push(item);
            }
          });
        }
      });
      setState((prevState) => ({ ...prevState, category3Options: createOptions(merged3, 'name', '_id') }));
    }

    // Hydrate Category 4 if Cat1+Cat2+Cat3 are already selected (support multi Cat1/Cat2/Cat3)
    if (
      Array.isArray(selectedSuggestion_Category1) &&
      selectedSuggestion_Category1.length &&
      Array.isArray(selectedSuggestion_Category2) &&
      selectedSuggestion_Category2.length &&
      Array.isArray(selectedSuggestion_Category3) &&
      selectedSuggestion_Category3.length
    ) {
      const c1s = selectedSuggestion_Category1;
      const c2s = selectedSuggestion_Category2;
      const c3s = selectedSuggestion_Category3;
      const calls4 = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          c3s.forEach((c3) => {
            calls4.push(
              dispatch(
                suggestionTodoListAction.fetchCategoryAction({
                  categoryType: 4,
                  loggedInUserId: userId,
                  categoryOneId: c1,
                  categoryTwoId: c2,
                  categoryThreeId: c3,
                  isActive: 1,
                }),
              ),
            );
          });
        });
      });
      const results4 = await Promise.all(calls4);
      const merged4 = [];
      const seen4 = new Set();
      results4.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen4.has(key)) {
              seen4.add(key);
              merged4.push(item);
            }
          });
        }
      });
      setState((prevState) => ({ ...prevState, category4Options: createOptions(merged4, 'name', '_id') }));
    }
  }, [dispatch, userId, selectedSuggestion_Category1, selectedSuggestion_Category2, selectedSuggestion_Category3]);

  // all useEffect
  // call all api's for filter section to fill the dropdown list
  useEffect(() => {
    const callFn = async () => {
      const BrandRes = isSupplierLoggedIn
        ? await dispatch(getSupplierBrandsAction(userId))
        : await dispatch(
            suggestionTodoListAction.fetchAllBrandAction({
              loggedInUserId: userId,
            }),
          );
      await fetchCategories();

      const sesonres = await dispatch(suggestionTodoListAction.getSeasonsAction({ loggedInUserId: userId }));

      if (!isSupplierLoggedIn) {
        let supp_list = [];

        if (isCompanyUserLoggedIn) {
          supp_list = await dispatch(suggestionTodoListAction.fetchUserSuppliersAction());

          supp_list = supp_list?.suppliers;
          supp_list = supp_list?.length
            ? supp_list.map((ele) => {
                if (ele.companies.length) {
                  ele.name = ele.companies[0].supplierName;
                }
                return ele;
              })
            : [];
        } else {
          supp_list = await dispatch(
            suggestionTodoListAction.getSuppliers({
              loggedInUserId: userId,
            }),
          );
        }
        if (checkArray(supp_list)) {
          const supplier_Options = createOptions(supp_list, 'name', '_id');
          setState((prevState) => ({
            ...prevState,
            supplierOptions: supplier_Options,
          }));
        }
      }

      const mainSample = await dispatch(
        suggestionTodoListAction.getMainSamplesAction({
          loggedInUserId: userId,
          isActive: true,
        }),
      );
      const otherSample = await dispatch(
        suggestionTodoListAction.getOtherSamplesAction({
          loggedInUserId: userId,
          isActive: true,
        }),
      );

      if (checkArray(mainSample) || checkArray(otherSample)) {
        const mergeSamples = concat(mainSample, otherSample);
        const sampleType_Options = createOptions(mergeSamples, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          sampleTypeOptions: sampleType_Options,
        }));
      }

      if (section) {
        setState((prevState) => ({
          ...prevState,
          sectionOptions: section,
        }));
      }

      const statusArr = await createOptions(['Approved', 'Rejected', 'Pending']);

      if (statusArr) {
        setState((prevState) => ({
          ...prevState,
          statusOptions: statusArr,
        }));
      }

      const actionType = createOptions(['Amendment', 'New']);

      if (actionType) {
        setState((prevState) => ({
          ...prevState,
          actionTypeOptions: actionType,
        }));
      }

      if (checkArray(sesonres)) {
        const season_Options = createOptions(sesonres, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          seasonOptions: season_Options,
        }));
      }
      if (checkArray(BrandRes)) {
        const brand_Options = createOptions(BrandRes, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          brandOptions: brand_Options,
        }));
      }
    };
    if (!isEmpty(userId)) callFn();
  }, [dispatch, userId]); // eslint-disable-line

  useEffect(() => {
    const callFn = async () => {
      if (isFetchingCatsRef.current) return;
      isFetchingCatsRef.current = true;
      try {
        const c1s = selectedCat1Ids;
        const c2s = selectedCat2Ids;
        const c3s = selectedCat3Ids;
        if (c1s.length && c2s.length && c3s.length && !isEmpty(userId)) {
          // Fetch Category 4 for every C1×C2×C3 combination and merge
          const calls = [];
          c1s.forEach((c1) => {
            c2s.forEach((c2) => {
              c3s.forEach((id) => {
                calls.push(
                  dispatch(
                    suggestionTodoListAction.fetchCategoryAction({
                      categoryType: 4,
                      loggedInUserId: userId,
                      categoryOneId: c1,
                      categoryTwoId: c2,
                      categoryThreeId: id,
                      isActive: 1,
                    }),
                  ),
                );
              });
            });
          });
          const results = await Promise.all(calls);
          const merged = [];
          const seen = new Set();
          results.forEach((res) => {
            const arr = Array.isArray(res?.data) ? res.data : res;
            if (Array.isArray(arr)) {
              arr.forEach((item) => {
                const key = String(item?._id ?? item?.value ?? '');
                if (key && !seen.has(key)) {
                  seen.add(key);
                  merged.push(item);
                }
              });
            }
          });
          const next = createOptions(merged, 'name', '_id');
          if (!areOptionIdsEqual(category4Options, next)) {
            setState((prevState) => ({ ...prevState, category4Options: next }));
          }

          // Also hydrate Category 3 options if empty
          if (!Array.isArray(category3Options) || category3Options.length === 0) {
            const calls3 = [];
            c1s.forEach((c1) => {
              c2s.forEach((c2) => {
                calls3.push(
                  dispatch(
                    suggestionTodoListAction.fetchCategoryAction({
                      categoryType: 3,
                      loggedInUserId: userId,
                      categoryOneId: c1,
                      categoryTwoId: c2,
                      isActive: 1,
                    }),
                  ),
                );
              });
            });
            const results3 = await Promise.all(calls3);
            const merged3 = [];
            const seen3 = new Set();
            results3.forEach((res) => {
              const arr = Array.isArray(res?.data) ? res.data : res;
              if (Array.isArray(arr)) {
                arr.forEach((item) => {
                  const key = String(item?._id ?? item?.value ?? '');
                  if (key && !seen3.has(key)) {
                    seen3.add(key);
                    merged3.push(item);
                  }
                });
              }
            });
            const next3 = createOptions(merged3, 'name', '_id');
            if (!areOptionIdsEqual(category3Options, next3)) {
              setState((prevState) => ({ ...prevState, category3Options: next3 }));
            }
          }
        } else if (c1s.length && c2s.length && !isEmpty(userId)) {
          // Fetch Category 3 for every C1×C2 combination and merge
          const calls = [];
          c1s.forEach((c1) => {
            c2s.forEach((c2) => {
              calls.push(
                dispatch(
                  suggestionTodoListAction.fetchCategoryAction({
                    categoryType: 3,
                    loggedInUserId: userId,
                    categoryOneId: c1,
                    categoryTwoId: c2,
                    isActive: 1,
                  }),
                ),
              );
            });
          });
          const results = await Promise.all(calls);
          const merged = [];
          const seen = new Set();
          results.forEach((res) => {
            const arr = Array.isArray(res?.data) ? res.data : res;
            if (Array.isArray(arr)) {
              arr.forEach((item) => {
                const key = String(item?._id ?? item?.value ?? '');
                if (key && !seen.has(key)) {
                  seen.add(key);
                  merged.push(item);
                }
              });
            }
          });
          const next = createOptions(merged, 'name', '_id');
          if (!areOptionIdsEqual(category3Options, next)) {
            setState((prevState) => ({ ...prevState, category3Options: next }));
          }
        }
      } finally {
        isFetchingCatsRef.current = false;
      }
    };
    callFn();
  }, [selCat1Key, selCat2Key, selCat3Key, dispatch, userId]);

  const [sta, setSta] = useState({
    status: null,
    actionType: null,
    section: null,
  });
  return (
    <button
      className={isApply ? 'filter-btn filter-button_delete active ' : 'filter-btn filter-button_delete '}
      type="button"
      title={t('TAKING_DATES.SUGGESTION_FILTER')}
    >
      <OverlayTrigger
        placement="bottom"
        trigger="click"
        rootClose
        overlay={
          <Popover className="custom-popover horizontal-view">
            <Popover.Content>
              <div className="product-costing-filter">
                <div className="filter-text">
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="brand"
                        options={brandOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={brandOptions.filter((val) => !isEmpty(selectedSuggestion_Brand) && selectedSuggestion_Brand.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_BRAND'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_BRAND')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedSuggestion_Brand',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  {subBrandAccess && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="subBrand"
                          options={subBrandOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={subBrandOptions.filter((val) => !isEmpty(selectedSuggestion_SubBrand) && selectedSuggestion_SubBrand.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: tReports('TODO_COMMON.SELECT_SUBBRAND'),
                          }}
                          labelledBy={tReports('TODO_COMMON.SELECT_SUBBRAND')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selectedSuggestion_SubBrand',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  {catBasedOnPTData && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="productType"
                          options={productTypeOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={productTypeOptions.filter(
                            (opt) => Array.isArray(selectedSuggestion_ProductType) && selectedSuggestion_ProductType.includes(opt._id),
                          )}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                          onChange={(selected) =>
                            handleSelectChange(Array.isArray(selected) ? selected.map((opt) => opt?._id).filter(Boolean) : [], 'selectedSuggestion_ProductType')
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category1"
                        options={catBasedOnPTData ? category1Filtered : category1Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category1Filtered : category1Options).filter(
                          (val) => !isEmpty(selectedSuggestion_Category1) && selectedSuggestion_Category1.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedSuggestion_Category1',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category2"
                        options={catBasedOnPTData ? category2Filtered : category2Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category2Filtered : category2Options).filter(
                          (val) => !isEmpty(selectedSuggestion_Category2) && selectedSuggestion_Category2.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedSuggestion_Category2',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category3"
                        options={catBasedOnPTData ? category3Filtered : category3Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category3Filtered : category3Options).filter(
                          (val) => !isEmpty(selectedSuggestion_Category3) && selectedSuggestion_Category3.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('SPEC_TEMP_LIBRARY.CATEGORY3_PLACEHOLDER'),
                        }}
                        labelledBy={t('SPEC_TEMP_LIBRARY.CATEGORY3_PLACEHOLDER')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedSuggestion_Category3',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category4"
                        options={catBasedOnPTData ? category4Filtered : category4Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category4Filtered : category4Options).filter(
                          (val) => !isEmpty(selectedSuggestion_Category4) && selectedSuggestion_Category4.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedSuggestion_Category4',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="season"
                        options={seasonOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={seasonOptions.filter((val) => !isEmpty(selectedSuggestion_Season) && selectedSuggestion_Season.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SEASON'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SEASON')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedSuggestion_Season',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <Select
                        isClearable="true"
                        classNamePrefix="common-select"
                        name="section"
                        options={sectionOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        // getOptionLabel={(option) => `${option.label}`}
                        // getOptionValue={(option) => `${option.value}`}
                        value={sta?.section}
                        onChange={(selected) => {
                          setSta({ ...sta, section: selected });

                          handleSelectChange(selected ? selected.value : null, 'selectedSuggestion_Section');
                        }}
                        styles={customStyles}
                        placeholder={t('TAKING_DATES.SELECT_SECTION')}
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <Select
                        isClearable="true"
                        classNamePrefix="common-select"
                        name="status"
                        options={statusOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        getOptionLabel={(option) => `${option.label}`}
                        getOptionValue={(option) => `${option.value}`}
                        value={sta.status}
                        onChange={(selected) => {
                          setSta({ ...sta, status: selected });
                          handleSelectChange(selected ? selected.value : null, 'selectedSuggestion_Status');
                        }}
                        styles={customStyles}
                        placeholder={t('PROGRESS_INDICATOR.SELECT_STATUS')}
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <Select
                        isClearable="true"
                        classNamePrefix="common-select"
                        name="actionType"
                        options={actionTypeOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        getOptionLabel={(option) => `${option.label}`}
                        getOptionValue={(option) => `${option.value}`}
                        value={sta.actionType}
                        onChange={(selected) => {
                          setSta({ ...sta, actionType: selected });

                          handleSelectChange(selected ? selected.value.toLowerCase() : null, 'selectedSuggestion_ActionType');
                        }}
                        styles={customStyles}
                        placeholder={t('TAKING_DATES.SELECTION_TYPE')}
                      />
                    </div>
                  </FormGroup>
                  {!isSupplierLoggedIn && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="supplier"
                          options={supplierOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={supplierOptions.filter((val) => !isEmpty(selectedSuggestion_Supplier) && selectedSuggestion_Supplier.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SUPLIERS'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selectedSuggestion_Supplier',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  {licensorAccess && (
                    <>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={licensorOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licensorOptions.filter((val) => checkArray(selectedLicensor) && selectedLicensor.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSOR'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSOR')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicensor',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={licenseOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licenseOptions.filter((val) => checkArray(selectedLicense) && selectedLicense.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSE'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSE')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicense',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            options={likenessOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={likenessOptions.filter((val) => checkArray(selectedLikeness) && selectedLikeness.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LIKENESS'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LIKENESS')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLikeness',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                    </>
                  )}
                </div>

                <div className="d-flex justify-content-end">
                  <button type="button" aria-label="Clear" className="btn p-0 d-inline-flex" onClick={clearAll}>
                    <span className="icon-Clear-New f-24" />
                  </button>
                  <button type="button" aria-label="Apply" className="btn p-0 d-inline-flex ml-16" onClick={applyFilter}>
                    <span className="icon-Tick-1 f-28" />
                  </button>
                </div>
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        <i className="icon-Filter f-26" />
      </OverlayTrigger>
    </button>
  );
}

export default FilterSuggestionList;
